import MoneyInput from '../../components/MoneyInput';
import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getSellerInterest, addNetSheetToInterest, ownsInterest, interestPrice, fullName } from '../../services/sellerInterestService';
import { SAMPLE_PIPELINE } from '../../data/samplePipeline';
import { useNavigate } from 'react-router-dom';
import { computeNetSheet, NET_SHEET_DEFAULTS } from '../../shared/netSheet';
import { page, wrap, card, h1, h2, muted, grid, label, input, row, bigRow, backBtn, money, num } from './ui';
import UnderConstructionBanner from '../../components/UnderConstructionBanner';

export default function SellerNetSheet() {
  const navigate = useNavigate();
  const [v, setV] = useState({ ...NET_SHEET_DEFAULTS });
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const { user, userRole } = useAuth();
  const params = new URLSearchParams(window.location.search);
  const siId = params.get('sellerInterest');
  const sheetKey = params.get('sheet');
  const [si, setSi] = useState(null);
  const [saveMsg, setSaveMsg] = useState('');
  // Opened from a seller (Pipeline): prefill the price; only the agent's own sellers (Admin: all).
  useEffect(() => {
    if (!siId) return;
    const sample = SAMPLE_PIPELINE.find((x) => x.id === siId && x.kind === 'seller');
    const apply = (r) => {
      setSi(r);
      const saved = sheetKey && r.netSheets?.[sheetKey]?.values;
      setV((prev) => saved ? { ...prev, ...saved } : { ...prev, price: String(interestPrice(r) || prev.price) });
    };
    if (sample) { apply(sample); return; }
    getSellerInterest(siId).then((r) => { if (ownsInterest(r, user?.uid, userRole)) apply(r); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siId, user?.uid, userRole]);
  const saveToSeller = async () => {
    if (!si) return;
    if (si.sample) { setSaveMsg('Sample record: nothing is saved.'); return; }
    try {
      await addNetSheetToInterest(si.id, { title: `Net sheet at ${money(num(v.price))}`, price: num(v.price), net: Math.round(net), values: v, createdBy: user?.uid || '' });
      setSaveMsg('Saved to the seller profile.');
    } catch (e) { setSaveMsg('Could not save: ' + (e.message || e)); }
  };

  // Same math as the MCP seller_net_sheet tool (src/shared/netSheet.js).
  const { price, sellerComm, buyerComm, docStamps, ownerTitleCost, title, closing, payoff, liens, hoa, proration, repairs, credits, net } = computeNetSheet(v);

  return (
    <div style={page}>
      <div style={wrap}>
        <button onClick={() => navigate('/tools')} style={backBtn}>← Back to tools</button>
        <h1 style={h1}>Seller Net Sheet</h1>
        {si && (
          <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} className="no-print">
            <div>For {fullName(si)}{si.propertyAddress ? ` - ${si.propertyAddress}` : ''}{si.sample ? ' (Sample record: nothing is saved.)' : ''}</div>
            <div><button style={backBtn} onClick={saveToSeller}>Save to seller profile</button>{saveMsg && <span style={{ marginLeft: 8, fontSize: 13 }}>{saveMsg}</span>}</div>
          </div>
        )}
        <div className="no-print"><UnderConstructionBanner /></div>
        <p style={muted}>Estimated seller proceeds. Florida doc stamps on the deed are usually 0.70% of the sale price; confirm all figures with the title company.</p>

        <div style={card}>
          <h2 style={h2}>Sale</h2>
          <div style={grid}>
            <div><span style={label}>Sale price</span><MoneyInput style={input} value={v.price} onChange={set('price')} placeholder="650000" /></div>
            <div><span style={label}>Listing commission %</span><input style={input} type="number" step="0.01" value={v.sellerCommPct} onChange={set('sellerCommPct')} /></div>
            <div><span style={label}>Buyer agent commission %</span><input style={input} type="number" step="0.01" value={v.buyerCommPct} onChange={set('buyerCommPct')} /></div>
            <div><span style={label}>Doc stamps %</span><input style={input} type="number" step="0.01" value={v.docStampsPct} onChange={set('docStampsPct')} /></div>
          </div>
        </div>

        <div style={card}>
          <h2 style={h2}>Costs and payoffs</h2>
          <div style={grid}>
            <div>
              <span style={label}>Property type</span>
              <select style={input} value={v.propertyType} onChange={(e) => setV({ ...v, propertyType: e.target.value })}>
                <option value="resale">Resale</option>
                <option value="new">New construction</option>
              </select>
            </div>
            <div>
              <span style={label}>Owner's title policy paid by</span>
              <select style={input} value={v.ownerTitlePayer} onChange={set('ownerTitlePayer')}>
                <option value="seller">Seller</option>
                <option value="buyer">Buyer</option>
              </select>
            </div>
            <div><span style={label}>Owner's title policy (blank = Florida rate)</span><input style={input} type="number" value={v.ownerTitle} onChange={set('ownerTitle')} /></div>
            <div><span style={label}>Title fees (seller's share)</span><input style={input} type="number" value={v.title} onChange={set('title')} /></div>
            <div><span style={label}>Closing / settlement fee</span><input style={input} type="number" value={v.closing} onChange={set('closing')} /></div>
            <div><span style={label}>Mortgage payoff</span><input style={input} type="number" value={v.payoff} onChange={set('payoff')} /></div>
            <div><span style={label}>Other liens or payoffs</span><input style={input} type="number" value={v.otherLiens} onChange={set('otherLiens')} /></div>
            <div><span style={label}>HOA transfer or estoppel fees</span><input style={input} type="number" value={v.hoaTransfer} onChange={set('hoaTransfer')} /></div>
            <div><span style={label}>Tax proration owed (if any)</span><input style={input} type="number" value={v.taxProration} onChange={set('taxProration')} /></div>
            <div><span style={label}>Repairs or credits you pay</span><input style={input} type="number" value={v.repairs} onChange={set('repairs')} /></div>
            <div><span style={label}>Other buyer credits or concessions</span><input style={input} type="number" value={v.credits} onChange={set('credits')} /></div>
          </div>
        </div>

        <div style={card}>
          <h2 style={h2}>Estimated net proceeds</h2>
          <div style={row}><span>Sale price</span><span>{money(price)}</span></div>
          <div style={row}><span>Listing commission</span><span>-{money(sellerComm)}</span></div>
          <div style={row}><span>Buyer agent commission</span><span>-{money(buyerComm)}</span></div>
          <div style={row}><span>Doc stamps</span><span>-{money(docStamps)}</span></div>
          {ownerTitleCost > 0 && <div style={row}><span>Owner's title policy (seller pays)</span><span>-{money(ownerTitleCost)}</span></div>}
          <div style={row}><span>Title and closing fees</span><span>-{money(title + closing)}</span></div>
          <div style={row}><span>HOA transfer, tax proration, repairs, credits</span><span>-{money(hoa + proration + repairs + credits)}</span></div>
          <div style={row}><span>Mortgage payoff and other liens</span><span>-{money(payoff + liens)}</span></div>
          <div style={bigRow}><span>Estimated cash to seller</span><span>{money(net)}</span></div>
        </div>
      </div>
    </div>
  );
}
